import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from './messages';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import {
  accentColorPrim,
  accentColorSecond,
  fontColorSecond,
  white,
} from '../../assets/styledAssets/vars';
import Button from '../../components/Button/Button.component';
import { GridColumn } from '@progress/kendo-react-grid';
import Moment from 'react-moment';
import { StyledTD } from '../Questionnaires/QuestionnairesList/styles';
import {
  CheckmarkDoneIcon,
  CloseCircleIcon,
  ExportIcon,
  ReportIcon,
} from '../../components/Icons/Icons.component';
import SpaceService from '../../service/Space.service';
import QuestionnarieService from '../../service/Questionnarie.service';
import AchievementsService from '../../service/Achievements.service';
import { Code, Roles } from '../../domains/app';
import DialogWrap from '../../components/DiaogWrap/DiaogWrap.component';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog.component';
import AccessCode from '../Groups/components/AccessCode/AccessCode.component';
import {
  createPersonalCode,
  getPersonalCodes,
} from '../../service/Integration.service';
import { StyledA } from './styles';
import { QuestionnaireRequest } from '../Questionnaires/questionnaires';
import moment from 'moment';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router';
import UserQuestionnaire from './components/UserQuestionnaire/UserQuestionnaire.component';

const connector = connect(
  (state: RootState) => ({
    profile: state.app.user,
  }),
  {},
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const HomePage: FunctionComponent<PropsFromRedux> = ({ profile }) => {
  const intl = useIntl();
  const [spaceInvitation, setSpaceInvitation] = useState<any[]>([]);
  const [spaceMembership, setSpaceMembership] = useState<any[]>([]);
  const [showAccessCodeDialog, setShowAccessCodeDialog] = useState(false);
  const [listCodes, setListCodes] = useState<Code[]>([]);
  const [listQuestionnaireRequests, setQuestionnaireRequests] = useState<
    QuestionnaireRequest[]
  >([]);
  const [listAchievmentRequests, setAchievmentRequests] = useState<any[]>([]);
  const history = useHistory();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [confirmationHandler, setConfirmationHandler] = useState<() => void>();

  useEffect(() => {
    SpaceService.fetchMyUserInvitations(profile?.email as string, 100, 0).then(
      (res) => {
        setSpaceInvitation((l) => l.concat(res.items));
      },
    );
    SpaceService.fetchMyAdminInvitations(profile?.email as string, 100, 0).then(
      (res) => {
        setSpaceInvitation((l) => l.concat(res.items));
      },
    );

    SpaceService.getSpaceMembership(profile?.id as string).then((res) => {
      setSpaceMembership(res);
    });

    getPersonalCodes(profile?.id as string).then((res) => {
      setListCodes(res);
    });

    QuestionnarieService.fetchQuestionnaireRequests(profile?.id as string).then(
      (res) => {
        setQuestionnaireRequests(res);
      },
    );

    AchievementsService.fetchAchievementRequest(profile?.id as string).then(
      (res) => {
        setAchievmentRequests(res);
      },
    );

    // eslint-disable-next-line
  }, []);

  const acceptInvitation = (id: string, role: Roles) => {
    const acceptApi =
      role & Roles.User
        ? SpaceService.acceptUserInvitation
        : SpaceService.acceptAdminInvitation;
    acceptApi(id, profile?.id as string).then(() => {
      setSpaceInvitation(spaceInvitation.filter((inv) => inv.id !== id));
      SpaceService.getSpaceMembership(profile?.id as string).then((res) => {
        setSpaceMembership(res);
      });
    });
  };

  const declineInvitation = (id: string) => {
    SpaceService.declineInvitation(id).then(() => {
      setSpaceInvitation(spaceInvitation.filter((inv) => inv.id !== id));
    });
  };

  const generateCode = useCallback(
    (m: number) => {
      createPersonalCode(profile?.id as string, m).then((code) => {
        const now = new Date();
        now.setMinutes(now.getMinutes() + m);
        setListCodes([...listCodes, { id: code, expired: now }]);
      });
    },
    [setListCodes, listCodes, profile?.id],
  );

  const leaveSpace = (spaceId: string) => {
    setShowConfirmation(true);
    setConfirmationText(intl.formatMessage(messages.leaveSpace));
    setConfirmationHandler(() => {
      return () => {
        SpaceService.leaveFromSpace(spaceId, profile?.id as string).then(() => {
          setSpaceMembership(
            spaceMembership.filter((s) => s.spaceId !== spaceId),
          );
        });
      };
    });
  };

  const navToFillQuestionnire = (
    requestId: string,
    questionnaireId: string,
  ) => {
    history.push(
      `${window.location.pathname}/questionnaire/${requestId}/${questionnaireId}`,
    );
  };

  const confirmAchievement = (id: string) => {
    setShowConfirmation(true);
    setConfirmationText(intl.formatMessage(messages.confirmAchievement));
    setConfirmationHandler(() => {
      return () => {
        AchievementsService.confirmAchievementRequest(id).then(() => {
          setAchievmentRequests(
            listAchievmentRequests.filter((s) => s.id !== id),
          );
        });
      };
    });
  };

  const declineAchievement = (id: string) => {
    setShowConfirmation(true);
    setConfirmationText(intl.formatMessage(messages.declineAchievement));
    setConfirmationHandler(() => {
      return () => {
        AchievementsService.declineAchievementRequest(id).then(() => {
          setAchievmentRequests(
            listAchievmentRequests.filter((s) => s.id !== id),
          );
        });
      };
    });
  };

  return (
    <>
      <Wrap
        width={'100%'}
        flexDir={'column'}
        flexWrap={'nowrap'}
        overflow={['auto', 'unset']}
      >
        <Wrap
          width={'100%'}
          margin={'0 0 3rem 0'}
          justifyCont={'space-between'}
          alignItems={'center'}
        >
          <Wrap fs={'3rem'} fw={'bold'} margin={['0 0 3rem']}>
            {intl.formatMessage(messages.title)}
          </Wrap>
          <Button primary onClick={() => setShowAccessCodeDialog(true)}>
            {intl.formatMessage(messages.createAccessCode)}
          </Button>
        </Wrap>

        <Wrap
          width={'100%'}
          flexDir={'column'}
          overflow={'auto'}
          flexWrap={'nowrap'}
        >
          <Wrap width={'100%'} margin={'0 0 3rem 0'}>
            <Wrap fs={'2rem'} fw={'bold'} color={accentColorPrim}>
              {intl.formatMessage(messages.spaceInvitations)}
            </Wrap>
          </Wrap>
          <Wrap hiddenXS width={'100%'}>
            <StyledTable style={{ height: '30rem' }} data={spaceInvitation}>
              <GridColumn
                field="invitedToSpace.name"
                title={intl.formatMessage(messages.space)}
              />
              <GridColumn
                field="invitedByPerson"
                title={intl.formatMessage(messages.invitedBy)}
                cell={(props) => (
                  <td>
                    {props.dataItem.invitedByPerson.firstName}{' '}
                    {props.dataItem.invitedByPerson.lastName}
                  </td>
                )}
              />
              <GridColumn
                field="invitedByPerson.email"
                title={intl.formatMessage(messages.email)}
              />
              <GridColumn
                field="invitedByPerson.phone"
                title={intl.formatMessage(messages.phone)}
                width="150px"
              />
              <GridColumn
                field="date"
                title={intl.formatMessage(messages.date)}
                width="150px"
                cell={(props) => (
                  <td>
                    <Moment format={'DD MMM YYYY'}>
                      {props.dataItem.date}
                    </Moment>
                  </td>
                )}
              />
              <GridColumn
                field="role"
                title={intl.formatMessage(messages.role)}
                width="150px"
                cell={(props) => (
                  <td>
                    <Wrap color={accentColorPrim} fw={'bold'}>
                      {props.dataItem.role === Roles.SpaceAdministrator
                        ? intl.formatMessage(messages.admin)
                        : intl.formatMessage(messages.member)}
                    </Wrap>
                  </td>
                )}
              />
              <GridColumn
                width="90px"
                cell={(props) => (
                  <StyledTD>
                    <Wrap alignItems={'center'}>
                      <Button
                        title={intl.formatMessage(messages.acceptInvitation)}
                        icon
                        transparent
                        onClick={() =>
                          acceptInvitation(
                            props.dataItem.id,
                            props.dataItem.role,
                          )
                        }
                      >
                        <CheckmarkDoneIcon
                          width={'1.8rem'}
                          color={accentColorPrim}
                        />
                      </Button>

                      <Button
                        icon
                        transparent
                        onClick={() => declineInvitation(props.dataItem.id)}
                        title={intl.formatMessage(messages.declineInvitation)}
                      >
                        <CloseCircleIcon
                          color={accentColorPrim}
                          width={'1.8rem'}
                        />
                      </Button>
                    </Wrap>
                  </StyledTD>
                )}
              />
            </StyledTable>
          </Wrap>
          <Wrap hiddenSM width={'100%'}>
            {spaceInvitation.map((item, index) => (
              <Wrap
                margin={'0 0 2rem 0'}
                width={'100%'}
                padding={'3rem'}
                br={'1.2rem'}
                flexDir={'column'}
                bgColor={white}
                key={`card-${index}`}
              >
                <Wrap grow={'1'} width={'100%'} justifyCont={'space-between'}>
                  <Wrap flexDir={'column'} grow={'1'}>
                    <Wrap fw={'bold'} fs={'2rem'}>
                      {item.invitedToSpace.name}
                    </Wrap>
                    <Wrap>
                      {intl.formatMessage(messages.date)}&nbsp;
                      <Moment format={'DD MMM YYYY'}>{item.date}</Moment>
                    </Wrap>
                  </Wrap>
                  <Wrap alignItems={'center'}>
                    <StyledA
                      style={{ marginRight: '1.4rem' }}
                      onClick={() => acceptInvitation(item.id, item.role)}
                    >
                      <CheckmarkDoneIcon
                        width={'2rem'}
                        color={accentColorPrim}
                      />
                    </StyledA>
                    <StyledA onClick={() => declineInvitation(item.id)}>
                      <CloseCircleIcon color={accentColorPrim} width={'2rem'} />
                    </StyledA>
                  </Wrap>
                </Wrap>
                <Wrap
                  alignItems={'center'}
                  flexWrap={'nowrap'}
                  width={'100%'}
                  margin={'2rem 0 3rem'}
                >
                  <Wrap
                    flexDir={'column'}
                    width={'50%'}
                    grow={'1'}
                    borders={[{ side: 'right', color: accentColorSecond }]}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.invitedBy)}
                    </Wrap>
                    <Wrap fw={'bold'}>
                      {item.invitedByPerson.firstName}{' '}
                      {item.invitedByPerson.lastName}
                    </Wrap>
                  </Wrap>
                  <Wrap
                    flexDir={'column'}
                    width={'50%'}
                    grow={'1'}
                    padding={'0 0 0 3rem'}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.role)}
                    </Wrap>
                    <Wrap>
                      <Wrap
                        color={white}
                        fs={'1.4rem'}
                        bgColor={accentColorPrim}
                        padding={'.3rem .5rem'}
                        br={'.5rem'}
                      >
                        {item.role === Roles.SpaceAdministrator
                          ? intl.formatMessage(messages.admin)
                          : intl.formatMessage(messages.member)}
                      </Wrap>
                    </Wrap>
                  </Wrap>
                </Wrap>
                <Wrap flexDir={'column'} width={'100%'}>
                  <Wrap
                    color={fontColorSecond}
                    fs={'1.4rem'}
                    margin={'0 0 1rem'}
                  >
                    {item.invitedByPerson.email || '-'}
                  </Wrap>
                  <Wrap color={fontColorSecond} fs={'1.4rem'}>
                    {item.invitedByPerson.phone || '-'}
                  </Wrap>
                </Wrap>
              </Wrap>
            ))}
          </Wrap>

          <Wrap width={'100%'} margin={'3rem 0'}>
            <Wrap fs={'2rem'} fw={'bold'} color={accentColorPrim}>
              {intl.formatMessage(messages.spaceMemberships)}
            </Wrap>
          </Wrap>
          <Wrap hiddenXS width={'100%'}>
            <StyledTable style={{ height: '30rem' }} data={spaceMembership}>
              <GridColumn
                field="space.name"
                title={intl.formatMessage(messages.space)}
              />
              <GridColumn
                field="enrolledByPerson"
                title={intl.formatMessage(messages.enrolledBy)}
                cell={(props) => (
                  <td>
                    {props.dataItem.enrolledByPerson.firstName}{' '}
                    {props.dataItem.enrolledByPerson.lastName}
                  </td>
                )}
              />
              <GridColumn
                field="enrolledByPerson.email"
                title={intl.formatMessage(messages.email)}
              />
              <GridColumn
                field="enrolledByPerson.phone"
                title={intl.formatMessage(messages.phone)}
                width="150px"
              />
              <GridColumn
                field="enrolled"
                title={intl.formatMessage(messages.startDate)}
                width="150px"
                cell={(props) => (
                  <td>
                    <Moment format={'DD MMM YYYY'}>
                      {props.dataItem.enrolled}
                    </Moment>
                  </td>
                )}
              />
              <GridColumn
                field="role"
                title={intl.formatMessage(messages.role)}
                width="150px"
                cell={(props) => (
                  <td>
                    <Wrap color={accentColorPrim} fw={'bold'}>
                      {props.dataItem.role & Roles.SpaceOwner
                        ? intl.formatMessage(messages.spaceOwner)
                        : props.dataItem.role & Roles.SpaceAdministrator
                        ? intl.formatMessage(messages.admin)
                        : intl.formatMessage(messages.member)}
                    </Wrap>
                  </td>
                )}
              />
              <GridColumn
                width="90px"
                cell={(props) => (
                  <StyledTD>
                    <Wrap alignItems={'center'}>
                      <Button
                        icon
                        transparent
                        onClick={() => leaveSpace(props.dataItem.spaceId)}
                        title={intl.formatMessage(messages.leaveFromSpace)}
                      >
                        <ExportIcon width={'1.8rem'} color={accentColorPrim} />
                      </Button>
                    </Wrap>
                  </StyledTD>
                )}
              />
            </StyledTable>
          </Wrap>
          <Wrap hiddenSM width={'100%'}>
            {spaceMembership.map((item, index) => (
              <Wrap
                margin={'0 0 2rem 0'}
                width={'100%'}
                padding={'3rem'}
                br={'1.2rem'}
                bgColor={white}
                flexDir={'column'}
                key={`card-${index}`}
              >
                <Wrap grow={'1'} width={'100%'} justifyCont={'space-between'}>
                  <Wrap flexDir={'column'} grow={'1'}>
                    <Wrap fw={'bold'} fs={'2rem'}>
                      {item.space.name}
                    </Wrap>
                    <Wrap>
                      {intl.formatMessage(messages.startDate)}&nbsp;
                      <Moment format={'DD MMM YYYY'}>{item.enrolled}</Moment>
                    </Wrap>
                  </Wrap>
                  <Wrap alignItems={'center'}>
                    <StyledA onClick={() => leaveSpace(item.spaceId)}>
                      <ExportIcon width={'2rem'} color={accentColorPrim} />
                    </StyledA>
                  </Wrap>
                </Wrap>
                <Wrap
                  width={'100%'}
                  alignItems={'center'}
                  flexWrap={'nowrap'}
                  margin={'2rem 0 3rem'}
                >
                  <Wrap
                    flexDir={'column'}
                    width={'50%'}
                    grow={'1'}
                    borders={[{ side: 'right', color: accentColorSecond }]}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.invitedBy)}
                    </Wrap>
                    <Wrap fw={'bold'}>
                      {item.enrolledByPerson.firstName}{' '}
                      {item.enrolledByPerson.lastName}
                    </Wrap>
                  </Wrap>
                  <Wrap
                    flexDir={'column'}
                    width={'50%'}
                    grow={'1'}
                    padding={'0 0 0 3rem'}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.role)}
                    </Wrap>
                    <Wrap>
                      <Wrap
                        color={white}
                        fs={'1.4rem'}
                        bgColor={accentColorPrim}
                        padding={'.3rem .5rem'}
                        br={'.5rem'}
                      >
                        {item.role === Roles.SpaceAdministrator
                          ? intl.formatMessage(messages.admin)
                          : intl.formatMessage(messages.member)}
                      </Wrap>
                    </Wrap>
                  </Wrap>
                </Wrap>
                <Wrap flexDir={'column'} width={'100%'}>
                  <Wrap
                    color={fontColorSecond}
                    fs={'1.4rem'}
                    margin={'0 0 1rem'}
                  >
                    {item.enrolledByPerson.email}
                  </Wrap>
                  <Wrap color={fontColorSecond} fs={'1.4rem'}>
                    {item.enrolledByPerson.phone}
                  </Wrap>
                </Wrap>
              </Wrap>
            ))}
          </Wrap>

          <Wrap width={'100%'} margin={'6rem 0 3rem'}>
            <Wrap fs={'2rem'} fw={'bold'} color={accentColorPrim}>
              {intl.formatMessage(messages.achievementsConfirmReqs)}
            </Wrap>
          </Wrap>
          <Wrap hiddenXS width={'100%'}>
            <StyledTable
              style={{ height: '30rem' }}
              data={listAchievmentRequests}
            >
              <GridColumn
                field="from"
                title={intl.formatMessage(messages.from)}
                cell={(props) => (
                  <td>
                    {props.dataItem.achievement.person.firstName}{' '}
                    {props.dataItem.achievement.person.lastName}
                  </td>
                )}
              />
              <GridColumn
                field="email"
                title={intl.formatMessage(messages.email)}
                cell={(props) => (
                  <td>{props.dataItem.achievement.person.email}</td>
                )}
              />
              <GridColumn
                field="achievement.event.name"
                title={intl.formatMessage(messages.event)}
              />
              <GridColumn
                field="achievement.levelOrResult"
                title={intl.formatMessage(messages.result)}
              />
              <GridColumn
                field="date"
                title={intl.formatMessage(messages.startDate)}
                cell={(props) => (
                  <td>
                    <Moment format={'DD MMM YYYY'}>
                      {props.dataItem.achievement.achievedOrStart}
                    </Moment>
                  </td>
                )}
              />
              <GridColumn
                width="90px"
                cell={(props) => (
                  <StyledTD>
                    <Wrap alignItems={'center'}>
                      <Button
                        icon
                        transparent
                        onClick={() => confirmAchievement(props.dataItem.id)}
                        title={intl.formatMessage(messages.acceptAchievement)}
                      >
                        <CheckmarkDoneIcon
                          width={'1.8rem'}
                          color={accentColorPrim}
                        />
                      </Button>
                      <Button
                        icon
                        transparent
                        onClick={() => declineAchievement(props.dataItem.id)}
                        title={intl.formatMessage(
                          messages.declineAchievementBtn,
                        )}
                      >
                        <CloseCircleIcon
                          width={'1.8rem'}
                          color={accentColorPrim}
                        />
                      </Button>
                    </Wrap>
                  </StyledTD>
                )}
              />
            </StyledTable>
          </Wrap>
          <Wrap hiddenSM width={'100%'}>
            {listAchievmentRequests.map((item, index) => (
              <Wrap
                margin={'0 0 2rem 0'}
                width={'100%'}
                padding={'3rem'}
                br={'1.2rem'}
                bgColor={white}
                key={`card-${index}`}
              >
                <Wrap
                  margin={'0 0 1rem 0'}
                  alignItems={'center'}
                  width={'100%'}
                  justifyCont={'space-between'}
                  grow={'1'}
                >
                  <Wrap fw={'bold'} fs={'2rem'}>
                    {item.achievement.person.firstName}{' '}
                    {item.achievement.person.lastName}
                  </Wrap>
                  <Wrap alignItems={'center'}>
                    <Button
                      icon
                      transparent
                      onClick={() => confirmAchievement(item.id)}
                    >
                      <CheckmarkDoneIcon
                        width={'1.8rem'}
                        color={accentColorPrim}
                      />
                    </Button>
                    <Button
                      icon
                      transparent
                      onClick={() => declineAchievement(item.id)}
                    >
                      <CloseCircleIcon
                        width={'1.8rem'}
                        color={accentColorPrim}
                      />
                    </Button>
                  </Wrap>
                </Wrap>
                <Wrap
                  width={'100%'}
                  justifyCont={'space-between'}
                  alignItems={'center'}
                >
                  <Wrap
                    flexDir={'column'}
                    width={'100%'}
                    maxWidth={'calc(50% - 1rem)'}
                    padding={'0 1rem 0 0'}
                    borders={[{ side: 'right', color: accentColorSecond }]}
                  >
                    <Wrap color={fontColorSecond} fs={'1.4rem'}>
                      {intl.formatMessage(messages.event)}
                    </Wrap>
                    <Wrap fw={'bold'}>{item.achievement.event.name}</Wrap>
                  </Wrap>
                  <Wrap
                    flexDir={'column'}
                    width={'100%'}
                    maxWidth={'calc(50% - 1rem)'}
                    padding={'0 1rem 0 0'}
                  >
                    <Wrap color={fontColorSecond} fs={'1.4rem'}>
                      {intl.formatMessage(messages.result)}
                    </Wrap>
                    <Wrap fw={'bold'}>
                      {item.achievement.levelOrResult || '-'}
                    </Wrap>
                  </Wrap>
                </Wrap>
                <Wrap flexDir={'column'} margin={'2rem 0 3rem'} width={'100%'}>
                  <Wrap
                    flexDir={'column'}
                    width={'calc(50% - 1rem)'}
                    grow={'1'}
                    borders={[{ side: 'right', color: accentColorSecond }]}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.startDate)}
                    </Wrap>
                    <Wrap fw={'bold'}>
                      <Moment format={'DD MMM YYYY'}>
                        {item.achievement.achievedOrStart}
                      </Moment>
                    </Wrap>
                  </Wrap>
                </Wrap>
                <Wrap flexDir={'column'}>
                  <Wrap
                    color={fontColorSecond}
                    fs={'1.4rem'}
                    margin={'0 0 1rem'}
                  >
                    {item.achievement.person.email || '-'}
                  </Wrap>
                  <Wrap color={fontColorSecond} fs={'1.4rem'}>
                    {item.achievement.person.phone || '-'}
                  </Wrap>
                </Wrap>
              </Wrap>
            ))}
          </Wrap>
          <Wrap width={'100%'} margin={'6rem 0 3rem'}>
            <Wrap fs={'2rem'} fw={'bold'} color={accentColorPrim}>
              {intl.formatMessage(messages.questinnaires)}
            </Wrap>
          </Wrap>
          <Wrap hiddenXS width={'100%'}>
            <StyledTable
              style={{ height: '30rem' }}
              data={listQuestionnaireRequests}
            >
              <GridColumn
                field="questionnaire.name"
                title={intl.formatMessage(messages.questionnaire)}
              />
              <GridColumn
                field="space.name"
                title={intl.formatMessage(messages.from)}
              />
              <GridColumn
                field="sentByPerson.firstName"
                title={intl.formatMessage(messages.sentBy)}
              />
              <GridColumn
                field="requested"
                title={intl.formatMessage(messages.requestDate)}
                cell={(props) => (
                  <td>
                    <Moment format={'DD MMM YYYY'}>
                      {props.dataItem.requested}
                    </Moment>
                  </td>
                )}
              />
              <GridColumn
                field="completed"
                title={intl.formatMessage(messages.completedDate)}
                cell={(props) => (
                  <td>
                    {' '}
                    {moment(props.dataItem.completed).isAfter(
                      new Date(2000, 0, 0),
                    ) ? (
                      <Moment format={'DD MMM YYYY'}>
                        {props.dataItem.completed}
                      </Moment>
                    ) : (
                      '-'
                    )}
                  </td>
                )}
              />
              <GridColumn
                width="90px"
                cell={(props) => (
                  <StyledTD>
                    <Wrap alignItems={'center'}>
                      {props.dataItem.isCompleted ? (
                        intl.formatMessage(messages.completed)
                      ) : (
                        <Button
                          icon
                          transparent
                          onClick={() =>
                            navToFillQuestionnire(
                              props.dataItem.id,
                              props.dataItem.questionnaire.id,
                            )
                          }
                        >
                          <ReportIcon width={'2rem'} color={accentColorPrim} />
                          <Wrap
                            margin={'0 0 0 1rem'}
                            color={accentColorPrim}
                            fs={'1.6rem'}
                          >
                            {intl.formatMessage(messages.open)}
                          </Wrap>
                        </Button>
                      )}
                    </Wrap>
                  </StyledTD>
                )}
              />
            </StyledTable>
          </Wrap>
          <Wrap hiddenSM width={'100%'}>
            {listQuestionnaireRequests.map((item, index) => (
              <Wrap
                margin={'0 0 2rem 0'}
                width={'100%'}
                padding={'3rem'}
                br={'1.2rem'}
                bgColor={white}
                flexDir={'column'}
                key={`card-${index}`}
              >
                <Wrap grow={'1'} width={'100%'} justifyCont={'space-between'}>
                  <Wrap flexDir={'column'} grow={'1'}>
                    <Wrap fw={'bold'} fs={'2rem'}>
                      {item.questionnaire.name}
                    </Wrap>
                    <Wrap>
                      {intl.formatMessage(messages.startDate)}&nbsp;
                      <Moment format={'DD MMM YYYY'}>{item.requested}</Moment>
                    </Wrap>
                  </Wrap>
                  <Wrap alignItems={'center'}>
                    {item.isCompleted ? (
                      intl.formatMessage(messages.completed)
                    ) : (
                      <Button
                        icon
                        transparent
                        onClick={() =>
                          navToFillQuestionnire(
                            item.id,
                            item.questionnaire.id as string,
                          )
                        }
                      >
                        <ReportIcon width={'2rem'} color={accentColorPrim} />
                        <Wrap
                          margin={'0 0 0 1rem'}
                          color={accentColorPrim}
                          fs={'1.6rem'}
                        >
                          {intl.formatMessage(messages.open)}
                        </Wrap>
                      </Button>
                    )}
                  </Wrap>
                </Wrap>
                <Wrap
                  alignItems={'center'}
                  flexWrap={'nowrap'}
                  margin={'2rem 0 3rem'}
                  width={'100%'}
                >
                  <Wrap
                    flexDir={'column'}
                    width={'calc(50% - 1rem)'}
                    grow={'1'}
                    borders={[{ side: 'right', color: accentColorSecond }]}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.sentBy)}
                    </Wrap>
                    <Wrap fw={'bold'}>{item.sentByPerson.firstName}</Wrap>
                  </Wrap>
                  <Wrap
                    flexDir={'column'}
                    width={'calc(50% - 1rem)'}
                    grow={'1'}
                    padding={'0 0 0 3rem'}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.from)}
                    </Wrap>
                    <Wrap>
                      <Wrap fw={'bold'}>{item.space.name}</Wrap>
                    </Wrap>
                  </Wrap>
                </Wrap>
                <Wrap
                  alignItems={'center'}
                  flexWrap={'nowrap'}
                  margin={'2rem 0 3rem'}
                  width={'100%'}
                >
                  <Wrap
                    flexDir={'column'}
                    width={'calc(50% - 1rem)'}
                    grow={'1'}
                    borders={[{ side: 'right', color: accentColorSecond }]}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.requestDate)}
                    </Wrap>
                    <Wrap fw={'bold'}>
                      <Moment format={'DD MMM YYYY'}>{item.requested}</Moment>
                    </Wrap>
                  </Wrap>
                  <Wrap
                    flexDir={'column'}
                    width={'calc(50% - 1rem)'}
                    grow={'1'}
                    padding={'0 0 0 3rem'}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.completedDate)}
                    </Wrap>
                    <Wrap fw={'bold'}>
                      {' '}
                      {moment(item.completed).isAfter(new Date(2000, 0, 0)) ? (
                        <Moment format={'DD MMM YYYY'}>{item.completed}</Moment>
                      ) : (
                        '-'
                      )}
                    </Wrap>
                  </Wrap>
                </Wrap>
              </Wrap>
            ))}
          </Wrap>
        </Wrap>
      </Wrap>
      {showAccessCodeDialog && (
        <DialogWrap
          children={
            <AccessCode
              onCancel={() => setShowAccessCodeDialog(false)}
              generateCode={generateCode}
              listCodes={listCodes}
            />
          }
        />
      )}
      {showConfirmation && confirmationHandler && (
        <ConfirmationDialog
          bodyText={confirmationText}
          handleCancel={() => setShowConfirmation(false)}
          handleConfirm={confirmationHandler}
        />
      )}
    </>
  );
};

const UserHomePage = connector(HomePage);

const connectorHome = connect((state: RootState) => ({}), {});

type PropsFromReduxHome = ConnectedProps<typeof connectorHome>;

const HomeContainer: FunctionComponent<PropsFromReduxHome> = () => {
  const { path } = useRouteMatch();
  return (
    <Wrap
      flexDir={'column'}
      flexWrap={'nowrap'}
      width={'100%'}
      overflow={'auto'}
    >
      <Switch>
        <Route exact path={`${path}`} render={() => <UserHomePage />} />
        <Route
          exact
          path={`${path}/questionnaire/:requestId/:questionId`}
          render={() => <UserQuestionnaire />}
        />
      </Switch>
    </Wrap>
  );
};

export default connector(HomeContainer);
